import React, { useState, useEffect } from 'react';
import { LogIn, Loader2, AlertCircle } from 'lucide-react';
import { portalLogin } from '@/lib/portal-api-client';
import { resolveBrandConfig, BrandConfig } from '@/lib/brand-config';
import BrandLogo from '@/components/BrandLogo';

interface PortalLoginFormProps {
  onLoginSuccess: (membershipId: string, membership: any) => void;
}

export function PortalLoginForm({ onLoginSuccess }: PortalLoginFormProps) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [brand, setBrand] = useState<BrandConfig | null>(null);
  const [autoLogin, setAutoLogin] = useState(false);
  const autoLoginAttemptedRef = React.useRef(false);

  const normalizePhone = (raw: string) => {
    return String(raw || '').replace(/\D/g, '');
  };

  // Load brand config on mount
  useEffect(() => {
    resolveBrandConfig(window.location.hostname)
      .then(setBrand)
      .catch((err) => {
        console.error('Failed to load brand config:', err);
        // Set a default brand if resolution fails
        setBrand({
          id: 'climate',
          brand_key: 'climate',
          domain: null,
          logo_url: null,
          primary_color: null,
          secondary_color: null,
          terms_url: null,
          enrollment_config: null,
        });
      });
  }, []);

  // Prefill from query params (used by the post-enrollment "Go to Member Portal" button)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qEmail = params.get('email');
    const qPhone = params.get('phone');
    const qAuto = params.get('autologin');

    if (qEmail) setEmail(qEmail);
    if (qPhone) setPhone(qPhone);
    if (qAuto === '1') setAutoLogin(true);
  }, []);

  async function doLogin() {
    if (!brand) {
      setError('Brand configuration not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await portalLogin({
        email: String(email || '').trim().toLowerCase(),
        phone: normalizePhone(phone),
        company_id: brand.id,
      });

      onLoginSuccess(result.membership_id, result.membership);
    } catch (err: any) {
      const raw = String(err?.message || '').trim();

      // The API commonly returns a 401 with a generic message.
      // Give a slightly more actionable hint for the most common failure mode.
      if (/invalid credentials|no active membership/i.test(raw)) {
        setError(
          "We couldn't find an active membership for that email + phone. Double-check you’re using the email from your membership receipt and the primary phone number on the account (digits only)."
        );
      } else {
        setError(raw || 'Invalid credentials. Please check your email and phone number.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    await doLogin();
  }

  // Auto-login (single attempt) when autologin=1 is present.
  useEffect(() => {
    if (!autoLogin) return;
    if (!brand) return;
    if (!email || !phone) return;
    if (autoLoginAttemptedRef.current) return;

    autoLoginAttemptedRef.current = true;
    void doLogin();
  }, [autoLogin, brand, email, phone]);

  if (!brand) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <BrandLogo className="h-16 mx-auto mb-4" alt={brand.brand_key || 'Climate Control Services'} />
          <h1 className="text-3xl font-bold text-gray-900">Membership Portal</h1>
          <p className="text-gray-600 mt-2">Access your membership dashboard</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                autoComplete="email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="(555) 555-5555"
                autoComplete="tel"
                inputMode="tel"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use the phone number on your membership account
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !phone}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing In...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Access My Membership
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Having trouble? Call{' '}
              <a
                href="tel:561-680-4467"
                className="font-semibold text-gray-600 hover:text-gray-800"
              >
                561-680-4467
              </a>{' '}
              for help accessing your membership.
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have a membership yet?{' '}
            <a href="/plans" className="text-blue-600 hover:text-blue-700 font-semibold">
              View Plans
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
